import { createStore, createEvent, createEffect, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { $notes, NotesState } from '@nx-frontend/shared/store/notes';
import { NoteDto } from '@nx-frontend/shared/types';

export const initialNoteState: NoteDto = {
  globalId: '',
  parentId: '',
  createdAt: 0,
  dateAdded: 0,
  dateUpdated: 0,
  updatedAt: 0,
  type: '',
  role: '',
  title: '',
  url: '',
  locationLat: 0,
  locationLng: 0,
  shared: false,
  favorite: false,
  lastChangeBy: 0,
  cntNotes: 0,
  size: 0,
  editnote: false,
  isEncrypted: false,
  isCompleted: false,
  workspaceId: '',
  isImported: false,
  isFullwidth: false,
  userId: 0,
  isReady: false,
  outliner: false,
};

interface GetNoteFxParams {
  storedNotes: NotesState;
  workspaceId: string;
  noteId: string;
}

export const getNoteFx = createEffect(
  async ({ storedNotes, workspaceId, noteId }: GetNoteFxParams): Promise<NoteDto | null> => {
    const storedNote = storedNotes.workspaces[workspaceId]?.entities[noteId];
    if (storedNote && !storedNote.isFetching && storedNote.isReady) {
      return Promise.resolve(storedNote);
    }
    const response = await axiosClient.get<NoteDto | null>(`/v2/api/workspaces/${workspaceId}/notes/${noteId}`);
    if (response.data) {
      return Promise.resolve(response.data);
    }
    return Promise.resolve(null);
  }
);
export const $note = createStore(initialNoteState);
export const setNoteEvent = createEvent<{ workspaceId: string; noteId: string }>();
export const updateCurrentNoteEvent = createEvent<Partial<NoteDto>>();
export const $isNoteFetching = createStore<boolean>(false);
$isNoteFetching.on(getNoteFx.pending, (_, isPending) => {
  return isPending;
});
//при срабатываении события updateCurrentNoteEvent обновляем текущий $note, а так же его в сторе $notes
$note.on(updateCurrentNoteEvent, (state, noteFields) => {
  if (noteFields.globalId && noteFields.globalId === state.globalId) {
    return { ...state, ...noteFields };
  }
  return state;
});
$notes.on(updateCurrentNoteEvent, (state, noteFields) => {
  if (
    !noteFields.globalId ||
    !noteFields.workspaceId ||
    !state.workspaces[noteFields.workspaceId]?.entities[noteFields.globalId]
  ) {
    return state;
  } else {
    const workspace = state.workspaces[noteFields.workspaceId];
    let entities = workspace.entities || {};
    const fetch = workspace.fetch || {};

    entities = {
      ...entities,
      [noteFields.globalId]: {
        ...state.workspaces[noteFields.workspaceId].entities[noteFields.globalId],
        ...noteFields,
      },
    };

    return {
      ...state,
      workspaces: {
        [noteFields.workspaceId]: {
          ...workspace,
          entities,
          fetch,
        },
      },
    };
  }
});
// при вызове события setNoteEvent вызываем эффект, который вернет заметку из стора $notes или с бэкенда
sample({
  source: {
    storedNotes: $notes,
  },
  clock: setNoteEvent,
  fn: ({ storedNotes }, { workspaceId, noteId }) => {
    return { storedNotes, workspaceId, noteId };
  },
  target: getNoteFx,
});
// подписываем стор $notes на эффект получения заметки
$notes.on(getNoteFx.doneData, (state, note: NoteDto | null) => {
  // если эффект вернул заметку и этой заметки еще нет в сторе, добавляем её в стор
  if (note === null || Boolean(state.workspaces[note.workspaceId]?.entities[note.globalId])) {
    return state;
  }
  const workspace = state.workspaces[note.workspaceId] || {};
  let entities = workspace.entities || {};
  const fetch = workspace.fetch || {};

  entities = {
    ...entities,
    [note.globalId]: {
      ...note,
      isFetching: false,
    },
  };

  return {
    ...state,
    workspaces: {
      [note.workspaceId]: {
        ...workspace,
        entities,
        fetch,
      },
    },
  };
});
//подписываем стор заметки на эффект получения заметки
$note.on(getNoteFx.doneData, (_, note) => (note !== null ? note : initialNoteState));
